import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { getStorage } from '@utils/localStorage';
import pageJump from '@projects/Shell/utils/pageJump';
import * as LX from '@utils/lxWrapper';
import * as Ids from '@utils/ids';
import cacheMenu from './utils/cacheMenu';
import createMenuObserver from './utils/menuObserver';
import { RootState } from '../../store';
import { IQueryMenuItem, MatchedMenuType } from '../../api/define';
import MenuTag from './MenuTag';
import MenuSubItem from './MenuSubItem';
import styles from './MenuItem.module.scss';

type MenuItemProps = {
    menu: IQueryMenuItem;
    pageHash: string;
    matchedMenu: MatchedMenuType;
    isMenuFolded: boolean;
    /** hover临时展开 */
    hoverMenuUnFold: boolean;
    setShellHash: (val: string) => void;
};

const { useState, useMemo, useCallback, useEffect } = React;
/** 左侧导航栏 - 菜单 */
const MenuItem: React.FC<MenuItemProps> = ({
    menu,
    pageHash,
    matchedMenu,
    isMenuFolded,
    hoverMenuUnFold,
    setShellHash,
}) => {
    //二级菜单ref 用于存储二级菜单列表的引用
    const subMenusRef = useRef<Array<HTMLDivElement>>([]);
    /** 是否显示二级菜单 - 点击 */
    const [subMenuClickVisible, setSubMenuClickVisible] = useState(false);

    const showSubMenus = useMemo(() => {
        // 左侧菜单展开时候 展示二级菜单
        if (!isMenuFolded && subMenuClickVisible) {
            return true;
        } else if (isMenuFolded && hoverMenuUnFold && subMenuClickVisible) {
            // 左侧菜单收起 && hover && 点击展开时 展示二级菜单
            return true;
        } else if (!menu.subMenus || isMenuFolded) {
            // 没有子菜单 || 左侧菜单收起时 隐藏二级菜单
            return false;
        } else {
            return false;
        }
    }, [menu.subMenus, subMenuClickVisible, isMenuFolded, hoverMenuUnFold]);

    useEffect(() => {
        if (matchedMenu.menuCode === menu.menuCode) setSubMenuClickVisible(true);
    }, [matchedMenu.menuCode]);

    /**
     * 增加对二级菜单列表元素监听
     */
    useEffect(() => {
        let observerInstance: IntersectionObserver | null = null;

        if (Array.isArray(menu.subMenus) && menu.subMenus.length > 0 && showSubMenus) {
            observerInstance = createMenuObserver({
                threshold: 0.5,
            });
            for (let i = 0; i < menu.subMenus.length; i++) {
                observerInstance.observe(subMenusRef.current[i]);
            }
        }
        return () => {
            //停止监听 并销毁监听实例
            if (observerInstance) {
                observerInstance.disconnect();
                observerInstance = null;
            }
        };
    }, [menu.subMenus, showSubMenus]);

    const onClickMenuItem = useCallback(() => {
        // 展开 | 收起二级菜单
        if (menu.subMenus && menu.subMenus.length) {
            setSubMenuClickVisible(!subMenuClickVisible);
        } else {
            LX.reportMC('b_waimai_e_0ylope0g_mc', 'c_waimai_e_fa4ps4ng', {
                acctId: Ids.getAcctId(),
                menuName: menu.menuName || '',
            });
            /* jscpd:ignore-start */
            const url = menu.url;
            if (url === pageHash) {
                // 点击同一菜单 页面刷新
                const thisWindow = pageJump.getThisWindow();
                const noJump = getStorage('nojump');
                if (noJump !== 'true') {
                    thisWindow.location.reload();
                }
            } else {
                if (['hot', 'new'].includes(menu.mask)) {
                    cacheMenu(menu.menuName);
                }
                // 更新页面hash
                setShellHash(url);
            }
            /* jscpd:ignore-end */
        }
    }, [menu, subMenuClickVisible, pageHash]);

    const getMenuIcon = (menuCode: string) => {
        if (!menuCode) return '';
        let iconName = menuCode.split('.')[1];

        /** 迎检改造临时兼容 */
        if (iconName === 'CustomerCommentTest') {
            iconName = 'Customer';
        }

        return `icon-wmbmenu-${iconName && iconName.toLocaleLowerCase()}`;
    };

    return (
        <li>
            <div
                className={`${styles.menuItem} ${
                    matchedMenu.menuCode === menu.menuCode ? styles.active : ''
                }`}
                onClick={onClickMenuItem}
            >
                <i className={`iconfont ${getMenuIcon(menu.menuCode)} ${styles.icon}`} />
                <span className={styles.txt}>
                    {menu.menuName}
                    <MenuTag
                        menuName={menu.menuName}
                        menuCode={menu.menuCode}
                        mask={menu.mask}
                        subMenus={menu.subMenus}
                    />
                </span>
            </div>
            {showSubMenus ? (
                <ul className="sub-menus">
                    {menu.subMenus.map((subMenu, index) => (
                        <div
                            id={subMenu.subMenuName}
                            ref={el => {
                                if (el) {
                                    subMenusRef.current[index] = el;
                                }
                            }}
                            key={subMenu.subMenuName}
                        >
                            <MenuSubItem subMenu={subMenu} />
                        </div>
                    ))}
                </ul>
            ) : (
                ''
            )}
        </li>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        pageHash: state.mainContent.pageHash,
        matchedMenu: state.shellLeftMenu.matchedMenu,
        hoverMenuUnFold: state.shellLeftMenu.hoverMenuUnFold,
        isMenuFolded: state.shellLeftMenu.isMenuFolded,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setShellHash: (val: string) => {
        dispatch({ type: 'mainContent/setShellHash', payload: val });
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(MenuItem);
